import {
  Container,
  Box,
  Typography,
  Grid,
  Button,
  Link,
  Card,
  TextField,
  useMediaQuery,
} from "@mui/material";
import CandidateList from "./CandidateList";
import CandidateListSkeleton from "./CandidateListSkeleton";
import { styled, keyframes } from "@mui/material/styles";
import ProfileCard from "./ProfileCard";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import { useTheme } from "@mui/material/styles";
import { useToast } from "@/utils/theme/useToast";
import {
  getCandidateCompany,
  hasDisplayCompany,
  hasUsableCandidateDetails,
  toRawCandidateFromSearchResult,
} from "@/lib/search/rawCandidates";

const DISCOVERY_PAGE_SIZE = 5;

const formatPossibleMatchCount = (count) => {
  if (count < 10) return count.toLocaleString("en-US");

  const magnitude = 10 ** Math.floor(Math.log10(count));
  const roundedCount = Math.floor(count / magnitude) * magnitude;
  const prefix = count >= 1_000 ? "+" : "";
  return `${prefix}${roundedCount.toLocaleString("en-US")}`;
};

const HeaderWithButton = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  width: "100%",
}));

const CleanButton = styled(Button)(({ theme }) => ({
  variant: "outlined",
  borderRadius: "35px",
  padding: "8px 22px",
  fontSize: "1rem",
  fontWeight: 600,
  backgroundColor: "transparent",
  color:
    theme.palette.mode === "light"
      ? theme.palette.background.accent
      : theme.palette.primary.main,
  border: `2px solid ${
    theme.palette.mode === "light"
      ? theme.palette.background.accent
      : theme.palette.primary.main
  }`,
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(0,0,0,0.04)"
        : theme.palette.primary.main,
    color:
      theme.palette.mode === "light"
        ? theme.palette.background.accent
        : "white",
  },
}));

const EmptyStateContainer = styled(Card)(({ theme }) => ({
  borderRadius: "12px",
  padding: theme.spacing(6),
  border: "none",
  boxShadow: "none",
  textAlign: "center",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3, 2),
    borderRadius: "10px",
  },
}));

const gentlePulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.62;
  }
`;

const LoadingProgressText = styled("span", {
  shouldForwardProp: (prop) => prop !== "pulse",
})(({ theme, pulse }) => ({
  display: "inline-flex",
  alignItems: "center",
  color: theme.palette.primary.main,
  whiteSpace: "pre-wrap",
  animation: pulse ? `${gentlePulse} 1.35s ease-in-out infinite` : "none",
}));

const LoadingProgressChar = styled("span", {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  transition: "color 180ms ease",
}));

const SearchResults = ({
  results,
  moreResults = [],
  rawCandidates = [],
  showTableNarrowHint = false,
  rcTotalCount = null,
  rcHasMore = true,
  savedHasMore = false,
  externalLimitReached = false,
  isLoadingMoreRC = false,
  enrichingId = null,
  enrichedUrls = new Set(),
  onShowMore,
  onLoadNextRCPage,
  onRetrySearch,
  retryInitialSearch = false,
  onEnrichOne,
  onNewSearch,
  onRefineSearch,
  searchType,
  searchQuery,
  nameQuery,
  companyQuery,
  externalSearchUnavailable: initialExternalSearchUnavailable = false,
  searchCooldown = null,
  onViewProfile,
  isNameOnly,
  suggestions = [],
  suggestionLabel = "",
  activeCardIndex = -1,
  onCardFocus,
  isGuestUser = true,
  userEmail = "",
}) => {
  const theme = useTheme();
  const router = useRouter();
  const toast = useToast();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // State for success dialog and request tracking
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState(userEmail);
  const [emailError, setEmailError] = useState("");
  const [requestCaseId, setRequestCaseId] = useState(null);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [showExternalCandidates, setShowExternalCandidates] = useState(false);
  const [noMoreExternalCandidates, setNoMoreExternalCandidates] = useState(
    false,
  );
  const [externalSearchUnavailable, setExternalSearchUnavailable] = useState(
    initialExternalSearchUnavailable,
  );
  const [isRetryingSearch, setIsRetryingSearch] = useState(false);
  const [loadingTextProgress, setLoadingTextProgress] = useState(0);
  const [visibleDiscoveryCount, setVisibleDiscoveryCount] = useState(DISCOVERY_PAGE_SIZE);
  const [cooldownClock, setCooldownClock] = useState(Date.now());
  const candidateListTopRef = useRef(null);
  const anotherPersonClickPendingRef = useRef(false);
  const displayNameQuery = (nameQuery || searchQuery).trim();
  const isPersonLookup =
    isNameOnly || searchType === "name" || Boolean(nameQuery);
  const isRoleLookup = !isPersonLookup && searchType === "discover";
  const isPeopleLookup = isPersonLookup || isRoleLookup;
  const loadingLabel = "Finding more matches...";
  const cooldownActive =
    searchCooldown && Date.parse(searchCooldown.retryAt) > cooldownClock;

  useEffect(() => {
    if (!searchCooldown) return undefined;
    setCooldownClock(Date.now());
    const interval = setInterval(() => setCooldownClock(Date.now()), 15_000);
    return () => clearInterval(interval);
  }, [searchCooldown]);

  const renderSearchCooldown = (hasDisplayedResults = false) => {
    if (!cooldownActive) return null;
    const minutes = Math.max(
      1,
      Math.ceil((Date.parse(searchCooldown.retryAt) - cooldownClock) / 60_000),
    );
    const retryTime = `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          mt: hasDisplayedResults ? 2 : 1.5,
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          maxWidth: "100%",
          px: 1.5,
          py: 1,
          borderRadius: 1.5,
          bgcolor: "action.hover",
          color: "text.secondary",
        }}
      >
        <AccessTimeOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
        <Typography variant="body2" component="span" sx={{ color: "inherit" }}>
          You&apos;re searching too fast.
          {searchCooldown.requiresSignIn && (
            <>
              {" "}
              <Link
                component="button"
                onClick={() => router.push("/login?callbackUrl=%2Fsearch")}
                sx={{ font: "inherit", verticalAlign: "baseline" }}
              >
                Sign in
              </Link>
              {" "}for more searches, or
            </>
          )}{" "}
          Try again in {retryTime}.
        </Typography>
      </Box>
    );
  };
  const getLinkedInVanity = (value = "") =>
    String(value || "").match(/linkedin\.com\/in\/([^/?#]+)/i)?.[1]?.toLowerCase() || "";
  const shouldShowCandidate = (candidate) => {
    const vanity = getLinkedInVanity(candidate.linkedinUrl || "");
    if (vanity && shownProfileVanities.has(vanity)) return false;
    // Must match what CandidateList accepts, otherwise its allowMissingCompany
    // never applies because these candidates were already discarded here.
    return isRoleLookup || isPersonLookup
      ? hasUsableCandidateDetails(candidate)
      : hasDisplayCompany(candidate);
  };
  const shownProfileVanities = new Set(
    results
      .map(
        (result) =>
          String(result.vanity || result.publicId || getLinkedInVanity(result.linkedinUrl || "")).toLowerCase(),
      )
      .filter(Boolean),
  );
  const totalLoadedResultCount =
    results.length + moreResults.length + rawCandidates.length;
  const knownMatchTotal =
    typeof rcTotalCount === "number" && rcTotalCount >= 0 ? rcTotalCount : null;
  const shouldShowTableNarrowHint =
    showTableNarrowHint ||
    (knownMatchTotal != null
      ? knownMatchTotal > 10
      : totalLoadedResultCount >= 100);
  const displayRawCandidates = rawCandidates.filter((candidate) => {
    return shouldShowCandidate(candidate);
  });
  const seenRoleCandidates = new Set();
  const roleTableCandidates = isPeopleLookup
    ? [...results, ...moreResults]
        .map((result) => ({
          ...toRawCandidateFromSearchResult(result),
          profileId: result.url || result.id || result.sub,
        }))
        .concat(displayRawCandidates)
        .filter((candidate) => {
          const key =
            getLinkedInVanity(candidate.linkedinUrl) || candidate.profileId || candidate.id ||
            `${candidate.firstName}|${candidate.lastName}|${candidate.company}`.toLowerCase();
          if (!hasUsableCandidateDetails(candidate) || seenRoleCandidates.has(key)) {
            return false;
          }
          seenRoleCandidates.add(key);
          return true;
        })
    : displayRawCandidates;
  const isCompletedEmptySearch = isPeopleLookup && roleTableCandidates.length === 0 &&
    !savedHasMore && !rcHasMore && !isLoadingMoreRC && !retryInitialSearch &&
    !externalSearchUnavailable && !initialExternalSearchUnavailable;
  const shouldOfferAnotherPerson =
    isPersonLookup &&
    (results.length > 1 ||
      (results.length === 1 && !results[0]?.externalEnriched));

  useEffect(() => {
    setVisibleDiscoveryCount(DISCOVERY_PAGE_SIZE);
    setShowExternalCandidates(false);
    setNoMoreExternalCandidates(false);
    setExternalSearchUnavailable(initialExternalSearchUnavailable);
    anotherPersonClickPendingRef.current = false;
  }, [displayNameQuery, isPersonLookup]);

  useEffect(() => {
    setExternalSearchUnavailable(initialExternalSearchUnavailable);
  }, [initialExternalSearchUnavailable, displayNameQuery]);

  useEffect(() => {
    if (!isMobile) return;
    if (!anotherPersonClickPendingRef.current) return;
    if (isLoadingMoreRC) return;
    if (!showExternalCandidates) return;

    const frameId = requestAnimationFrame(() => {
      candidateListTopRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      anotherPersonClickPendingRef.current = false;
    });

    return () => cancelAnimationFrame(frameId);
  }, [isMobile, isLoadingMoreRC, showExternalCandidates]);

  useEffect(() => {
    if (!isLoadingMoreRC) {
      setLoadingTextProgress(0);
      return;
    }

    setLoadingTextProgress(1);
    const intervalId = setInterval(() => {
      setLoadingTextProgress((prev) => {
        if (prev >= loadingLabel.length) {
          clearInterval(intervalId);
          return prev;
        }
        return prev + 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isLoadingMoreRC, loadingLabel.length]);

  const getEmptyStateContent = () => {
    if (externalSearchUnavailable) {
      return {
        title: "We couldn't complete your search",
        subtitle: "Something went wrong while looking for results.",
        primarySuggestion:
          "This can happen when your search takes longer than expected or when we are briefly unable to complete it. Your search is still available, so please try again in a moment.",
        actionText: "Try again",
      };
    }

    if (isPeopleLookup) {
      return {
        title: "No exact match found",
        subtitle: `No one matches “${searchQuery.trim()}”.`,
        primarySuggestion: isPersonLookup
          ? "Check the spelling, company, or city, or submit a request so we can help find the person you're looking for."
          : "Try refining the role, company, or city, or submit a request so we can help find matching people.",
        actionText: requestSubmitted ? "Request Submitted" : "Let Us Add Them",
      };
    }

    // For exact lookup misses, keep the user in Meex instead of sending them
    // to a public-profile URL search.
    if (
      searchType === "name" ||
      searchType === "company" ||
      searchType === "email"
    ) {
      // Use nameQuery for name searches, searchQuery for email searches
      const displayQuery =
        searchType === "name" || searchType === "company"
          ? nameQuery
          : searchQuery;

      return {
        title: "No exact match found",
        subtitle:
          searchType === "email"
            ? `We couldn't find anyone with the email "${displayQuery}" in our database`
            : `We couldn't find "${displayQuery}" in our database`,
        primarySuggestion:
          knownMatchTotal > 0
            ? "That name does appear in professional sources, but nothing matched closely enough to show. Check the spelling, or ask us to find and add them."
            : "Double-check the spelling, or ask us to find this person and add them to Meex.",
        actionText: requestSubmitted ? "Request Submitted" : "Let Us Add Them",
      };
    }

    // For LinkedIn searches that fail
    return {
      title: "They're Not Here Yet",
      subtitle: "We'll verify and add them quickly.",
      primarySuggestion:
        "Would you like us to add this person to our database? We'll search professional sources and make their profile available for future searches.",
      actionText: requestSubmitted ? "Request Submitted" : "Let Us Add Them",
    };
  };

  const emptyState = getEmptyStateContent();
  const EmptyStateWrapper = isMobile ? Box : EmptyStateContainer;
  const mobilePrimarySuggestion = isPeopleLookup && !externalSearchUnavailable
    ? isPersonLookup
      ? "Check the name, company, or city, or ask us to find them."
      : "Try another role, company, or city, or ask us to find matches."
    : emptyState.primarySuggestion;

  const handleActionClick = async () => {
    if (externalSearchUnavailable) {
      setIsRetryingSearch(true);
      try {
        const loadedCandidates = await onLoadNextRCPage?.();
        if (loadedCandidates?.externalSearchUnavailable) {
          setExternalSearchUnavailable(true);
        } else {
          setExternalSearchUnavailable(false);
        }
      } finally {
        setIsRetryingSearch(false);
      }
      return;
    }

    if (!requestSubmitted && !isSubmittingRequest) {
      submitRequest(isGuestUser ? "" : userEmail);
    }
  };

  const submitRequest = async (email = notificationEmail) => {
    const normalizedEmail = email.trim().toLowerCase();
    if (
      isGuestUser &&
      normalizedEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
    ) {
      setEmailError("Enter a valid email address so we can notify you.");
      return;
    }

    setIsSubmittingRequest(true);

    try {
      // Call API endpoint
      const response = await fetch("/api/admin/search-request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          // Only a linkedin search carries a real URL; other types would send
          // the raw query text and render as a broken link in moderation.
          linkedinUrl: searchType === "linkedin" ? searchQuery : "",
          searchQuery,
          searchType: searchType,
          nameQuery: nameQuery,
          companyQuery: companyQuery,
          requestSource: "search_page",
          userAgent: navigator.userAgent,
          notificationEmail: normalizedEmail,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to submit request");
      }

      // Mark request as submitted and show success modal
      setRequestCaseId(result.caseId);
      setRequestSubmitted(true);
      toast.show(
        "Request submitted",
        normalizedEmail
          ? "We'll email you when their profile is available."
          : "We've received the request and will look into adding their profile.",
      );
      if (isGuestUser) {
        setEmailError("");
        setShowEmailDialog(true);
      }
    } catch (error) {
      console.error("Failed to submit request:", error);
      toast.show("Request failed", "Please try again.", { type: "error" });
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const submitNotificationEmail = async () => {
    const normalizedEmail = notificationEmail.trim().toLowerCase();
    if (
      normalizedEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
    ) {
      setEmailError("Enter a valid email address or leave it blank.");
      return;
    }

    if (!normalizedEmail) {
      setShowEmailDialog(false);
      return;
    }

    setIsSubmittingRequest(true);
    try {
      const response = await fetch("/api/admin/search-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          caseId: requestCaseId,
          notificationEmail: normalizedEmail,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Failed to save email");
      }
      setShowEmailDialog(false);
      toast.show(
        "Email saved",
        "We'll email you when their profile is available.",
      );
    } catch (error) {
      console.error("Failed to save notification email:", error);
      toast.show(
        "Email not saved",
        "Your request was received. Please try again.",
        {
          type: "error",
        },
      );
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  if (
    !isPeopleLookup && externalSearchUnavailable &&
    (isRetryingSearch || isLoadingMoreRC)
  ) {
    return <CandidateListSkeleton query={searchQuery} />;
  }

  // Both candidate tables share this heading. Width matches CandidateSurface
  // so the text lines up with the table's left edge.
  const renderTableHeading = () => (
    <Box sx={{ width: "100%", maxWidth: "1040px", mx: "auto", mb: 2 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 560,
          color: theme.palette.text.primary,
          letterSpacing: 0,
          mb: 0.35,
        }}
      >
        {`People matching “${searchQuery.trim() || displayNameQuery}”`}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: "0.9rem",
        }}
      >
        {isMobile
          ? "Select a person or refine your search."
          : "Select a person, or refine your search with a company or city."}
      </Typography>
    </Box>
  );

  // RC People Search returns 3 profiles per request, so every candidate table
  // needs its own way to pull the next batch.
  const hasLoadedEveryMatch =
    !savedHasMore && knownMatchTotal != null && rawCandidates.length >= knownMatchTotal;
  const renderSeeMorePeople = () => (
    // Matches CandidateSurface so the control lines up with the table's left edge.
    <Box sx={{ width: "100%", maxWidth: "1040px", mx: "auto" }}>
      {(savedHasMore || (!noMoreExternalCandidates && !hasLoadedEveryMatch)) && (
        <Box sx={{ mt: 2.25, textAlign: "left" }}>
          <Button
            disableRipple
            disabled={isLoadingMoreRC || (cooldownActive && !savedHasMore)}
            onClick={async () => {
              const loadedCandidates = await onLoadNextRCPage?.();
              if (!loadedCandidates || loadedCandidates.stale) return;
              const hasLoadedDisplayCandidates = (
                loadedCandidates || []
              ).some((candidate) => shouldShowCandidate(candidate));
              if (!loadedCandidates?.stale && !hasLoadedDisplayCandidates &&
                  !loadedCandidates?.nextCursor && !loadedCandidates?.savedCursor &&
                  !loadedCandidates?.externalSearchUnavailable) {
                setNoMoreExternalCandidates(true);
              }
            }}
            sx={{
              color:
                theme.palette.mode === "light"
                  ? theme.palette.background.accent
                  : "primary.main",
              textTransform: "none",
              fontSize: { xs: "1.05rem", sm: "0.98rem" },
              fontWeight: 560,
              px: 0.5,
              minWidth: 0,
              opacity: isLoadingMoreRC ? 0.7 : 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 0.5,
              "&:hover": {
                background: "transparent",
                textDecoration: "underline",
              },
              "&:active": { background: "transparent" },
            }}
          >
            {isLoadingMoreRC ? (
              <LoadingProgressText
                pulse={loadingTextProgress >= loadingLabel.length}
              >
                {Array.from(loadingLabel).map((char, index) => (
                  <LoadingProgressChar
                    key={`${char}-under-${index}`}
                    active={index < loadingTextProgress}
                  >
                    {char}
                  </LoadingProgressChar>
                ))}
              </LoadingProgressText>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.4,
                }}
              >
                <span>See more matches</span>
                <ExpandMoreIcon
                  sx={{
                    fontSize: "1.2rem",
                    transition: "transform 0.2s ease",
                  }}
                />
              </Box>
            )}
          </Button>
        </Box>
      )}

      {!savedHasMore && (noMoreExternalCandidates || hasLoadedEveryMatch) && (
        <Typography
          variant="body2"
          sx={{
            mt: 1.2,
            textAlign: "left",
            color: theme.palette.text.secondary,
            fontSize: "0.86rem",
          }}
        >
          No other matches found.
        </Typography>
      )}
    </Box>
  );

  const renderPeopleResults = () => {
    const displayed = roleTableCandidates.slice(0, visibleDiscoveryCount);
    const singleProfile = roleTableCandidates.length === 1
      ? [...results, ...moreResults].find((profile) =>
          (profile.url || profile.id || profile.sub) === roleTableCandidates[0].profileId)
      : null;
    const hasHiddenResults = roleTableCandidates.length > visibleDiscoveryCount;
    const canSearchMore =
      savedHasMore || (rcHasMore && !noMoreExternalCandidates);
    const limitReached = externalLimitReached && !savedHasMore && !hasHiddenResults;
    const searchComplete = !externalLimitReached && !canSearchMore && !externalSearchUnavailable && !isLoadingMoreRC;
    const possibleMatchCount = searchComplete
      ? roleTableCandidates.length
      : knownMatchTotal == null
        ? null
        : Math.max(knownMatchTotal, roleTableCandidates.length);
    const loadMoreAction = (
      !cooldownActive &&
      (hasHiddenResults || canSearchMore || (externalSearchUnavailable && !limitReached) || isLoadingMoreRC) && (
          <Button
            variant="text"
            color="inherit"
            disabled={isLoadingMoreRC}
            onClick={async () => {
              if (retryInitialSearch) {
                await onRetrySearch?.();
                return;
              }
              const nextVisibleCount = displayed.length + DISCOVERY_PAGE_SIZE;
              const missing = Math.max(0, nextVisibleCount - roleTableCandidates.length);
              if (missing === 0 || (!canSearchMore && !externalSearchUnavailable)) {
                setVisibleDiscoveryCount(nextVisibleCount);
                return;
              }
              // Keep the current rows fixed until the whole next group is ready.
              setVisibleDiscoveryCount(displayed.length);
              const loaded = await onLoadNextRCPage?.({ minCandidates: missing });
              if (!loaded || loaded.stale) return;
              setVisibleDiscoveryCount(nextVisibleCount);
              if (loaded?.externalSearchUnavailable) {
                setExternalSearchUnavailable(true);
                return;
              }
              setExternalSearchUnavailable(false);
              if (!loaded?.length && !loaded?.nextCursor && !loaded?.savedCursor) {
                setNoMoreExternalCandidates(true);
              }
            }}
            sx={{ mt: 2, minHeight: 44, textTransform: "none", color: "text.primary", backgroundColor: "transparent", "&:hover, &.Mui-disabled": { backgroundColor: "transparent" } }}
          >
            {isLoadingMoreRC
              ? "Loading more people..."
              : "Load more"}
          </Button>
        )
    );
    return (
      <Box sx={{ width: "100%", maxWidth: "1040px", mx: "auto" }}>
        {renderTableHeading()}
        <Typography
          variant="body2"
          sx={{ mb: 2, color: "text.secondary", fontSize: isMobile ? "0.9rem" : "0.8125rem", lineHeight: 1.5 }}
          aria-live="polite"
        >
          {`${displayed.length} ${displayed.length === 1 ? "person" : "people"} shown`}
          {possibleMatchCount != null &&
            (possibleMatchCount === 0
              ? " · No possible matches"
              : ` · About ${formatPossibleMatchCount(possibleMatchCount)} possible ${possibleMatchCount === 1 ? "match" : "matches"}`)}
        </Typography>
        {singleProfile ? (
          <Grid container sx={{ mt: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <ProfileCard user={singleProfile} savedVariant />
            </Grid>
          </Grid>
        ) : displayed.length > 0 ? (
          <CandidateList
            candidates={displayed}
            footer={loadMoreAction}
            showNarrowSearchHint={limitReached ? false : shouldShowTableNarrowHint}
            isLoadingMore={isLoadingMoreRC}
            enrichingId={enrichingId}
            enrichedUrls={enrichedUrls}
            onEnrich={(candidate, options = {}) => {
              if (candidate.profileId) {
                const path = `/profile/${encodeURIComponent(candidate.profileId)}`;
                if (options.newTab) {
                  window.open(path, "_blank", "noopener,noreferrer");
                } else {
                  router.push(path);
                }
              } else {
                onEnrichOne?.(candidate, options);
              }
            }}
            onRefineSearch={onRefineSearch}
            query={searchQuery.trim()}
            allowMissingCompany
            narrowSearchHintMode={isPersonLookup ? "name" : "role"}
          />
        ) : !isLoadingMoreRC && !externalSearchUnavailable && !cooldownActive && (
          <Typography sx={{ my: 3 }}>
            {canSearchMore
              ? "Continue searching, or refine your search with a company or city."
              : "No matching people found. Try refining your search with a company or city."}
          </Typography>
        )}
        {renderSearchCooldown(displayed.length > 0)}
        {limitReached && (
          <Typography variant="body2" sx={{ mt: 3, color: "text.secondary", textAlign: "center" }} role="status">
            More people may match. Refine your search to continue.
          </Typography>
        )}
        {externalSearchUnavailable && !limitReached && !cooldownActive && (
          <Typography variant="body2" sx={{ mt: 2 }} role="status">
            {displayed.length > 0
              ? "We couldn't load more people. Your current results are still available."
              : "We couldn't complete the search. Please retry."}
          </Typography>
        )}
        {!singleProfile && displayed.length > 0 && !hasHiddenResults && !canSearchMore &&
          !limitReached && !externalSearchUnavailable && !isLoadingMoreRC && (
          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            No more matching people found.
          </Typography>
        )}
        {!displayed.length && suggestions.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
              {suggestionLabel || "Other people you can meet"}
            </Typography>
            <CandidateList
              candidates={suggestions.map((profile) => ({
                ...toRawCandidateFromSearchResult(profile), profileId: profile.url || profile.id || profile.sub,
              }))}
              allowMissingCompany
              onEnrich={(candidate, options = {}) => {
                const path = `/profile/${encodeURIComponent(candidate.profileId)}`;
                if (options.newTab) window.open(path, "_blank", "noopener,noreferrer");
                else router.push(path);
              }}
              onRefineSearch={onRefineSearch}
            />
          </Box>
        )}
        {(singleProfile || displayed.length === 0) && loadMoreAction}
      </Box>
    );
  };

  const NewSearchButton = isMobile ? Button : CleanButton;

  return (
    <>
      <Container
        maxWidth="lg"
        sx={{ py: { xs: 1, md: 4 }, minHeight: "calc(100dvh - 170px)" }}
      >
        {/* Header section */}
        <Box sx={{ mt: { xs: 0, md: 8 }, mb: { xs: 3, md: 2 } }}>
          <HeaderWithButton>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            ></Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Button
                onClick={onRefineSearch}
                sx={{
                  ...(isMobile && { opacity: 0.65, "&:hover, &:focus-visible": { opacity: 1, backgroundColor: "transparent" } }),
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "text.primary",
                  borderRadius: "35px",
                  minHeight: 44,
                  px: 2,
                }}
              >
                Refine search
              </Button>
              <NewSearchButton
                onClick={onNewSearch}
                startIcon={isMobile ? undefined : <SearchIcon />}
                sx={{
                  textTransform: "none",
                  px: { xs: 1.5, md: 2.75 },
                  ...(isMobile && {
                    minHeight: 44,
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "text.primary",
                    backgroundColor: "transparent",
                    "&:hover": { backgroundColor: "transparent" },
                  }),
                }}
              >
                New search
              </NewSearchButton>
            </Box>
          </HeaderWithButton>
        </Box>

        {isPeopleLookup && !isCompletedEmptySearch ? renderPeopleResults() : retryInitialSearch && externalSearchUnavailable ? (
          <Box sx={{ my: 3 }}>
            {renderSearchCooldown() || (
              <>
                <Typography sx={{ mb: 2 }} role="status">We couldn't complete the search. Please retry.</Typography>
                <Button onClick={onRetrySearch} sx={{ textTransform: "none" }}>Retry</Button>
              </>
            )}
          </Box>
        ) : isCompletedEmptySearch || results.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent:
                displayRawCandidates.length > 0 ? "flex-start" : "center",
              minHeight: { xs: "auto", md: "60vh" },
              px: { xs: 2, md: 0 },
              py: { xs: 1, md: 0 },
            }}
          >
            {/* Three cases:
                1) rawCandidates found — show a candidate picker
                2) suggestions found — compact "no exact match" header
                3) nothing found — compact mobile empty state or desktop card */}
            {displayRawCandidates.length > 0 ? (
              renderTableHeading()
            ) : suggestions.length > 0 && !isCompletedEmptySearch ? (
              <Box
                sx={{
                  maxWidth: "1200px",
                  width: "100%",
                  mx: { xs: 1, md: "auto" },
                  mb: 3,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 0.5,
                    color: theme.palette.text.primary,
                  }}
                >
                  No exact match found
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  We couldn&apos;t find who you&apos;re looking for — but here
                  are professionals you can book right now.
                </Typography>
              </Box>
            ) : (
              <EmptyStateWrapper
                sx={{
                  maxWidth: "1200px",
                  width: "100%",
                  mx: { xs: 1, md: "auto" },
                  ...(isMobile && {
                    p: 0,
                    borderRadius: 0,
                    backgroundColor: "transparent",
                    textAlign: "left",
                  }),
                }}
              >
                <Typography
                  variant={isMobile ? "h5" : "h3"}
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    mt: { xs: 0, md: 2 },
                    mb: { xs: 0.5, md: 1 },
                    ml: { xs: 0, md: 0 },
                    fontSize: { xs: "1.5rem", md: "2.5rem" },
                    textAlign: "left",
                    lineHeight: { xs: 1.3, md: 1.2 },
                  }}
                >
                  {emptyState.title}
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: { xs: 1.5, md: 4 },
                    maxWidth: "1200px",
                    mx: "auto",
                    lineHeight: 1.6,
                    textAlign: "left",
                    px: 0,
                  }}
                >
                  {emptyState.subtitle}
                </Typography>

                {/* Primary suggestion with action button */}
                <Box
                  sx={{
                    mb: { xs: 2.5, md: 4 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.primary,
                      mb: { xs: 1.5, md: 2 },
                      textAlign: "left",
                      fontSize: { xs: "0.95rem", md: "1rem" },
                      lineHeight: 1.5,
                    }}
                  >
                    {isMobile
                      ? mobilePrimarySuggestion
                      : emptyState.primarySuggestion}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                      mt: { xs: 0.5, md: 1 },
                      width: "100%",
                    }}
                  >
                    {/* Primary action */}
                    {isMobile ? (
                      <Button
                        variant="contained"
                        onClick={handleActionClick}
                        disabled={
                          requestSubmitted ||
                          isSubmittingRequest ||
                          isLoadingMoreRC
                        }
                        sx={{
                          width: "auto",
                          minWidth: 210,
                          minHeight: 48,
                          alignSelf: "flex-start",
                          px: 2.5,
                          borderRadius: "50px",
                          textTransform: "none",
                          fontWeight: 600,
                          backgroundColor:
                            requestSubmitted || isSubmittingRequest
                              ? theme.palette.grey[400]
                              : theme.palette.mode === "light"
                                ? theme.palette.common.black
                                : theme.palette.grey[100],
                          color:
                            theme.palette.mode === "light"
                              ? theme.palette.common.white
                              : theme.palette.grey[900],
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "light"
                                ? theme.palette.grey[900]
                                : theme.palette.grey[200],
                            boxShadow: "none",
                          },
                          "&.Mui-disabled": {
                            color: theme.palette.text.secondary,
                          },
                        }}
                      >
                        {isSubmittingRequest
                          ? "Submitting..."
                          : isPeopleLookup &&
                            !externalSearchUnavailable &&
                            !requestSubmitted
                            ? isPersonLookup
                              ? "Ask Meex to find them"
                              : "Ask Meex to find matches"
                            : emptyState.actionText}
                      </Button>
                    ) : (
                      <CleanButton
                        onClick={handleActionClick}
                        disabled={
                          requestSubmitted ||
                          isSubmittingRequest ||
                          isLoadingMoreRC
                        }
                        sx={{
                          px: 4,
                          py: 1.5,
                          width: "300px",
                          fontSize: "1.1rem",
                          opacity: isLoadingMoreRC ? 0.6 : 1,
                          cursor:
                            requestSubmitted ||
                            isSubmittingRequest ||
                            isLoadingMoreRC
                              ? "not-allowed"
                              : "pointer",
                          backgroundColor:
                            requestSubmitted || isSubmittingRequest
                              ? theme.palette.grey[400]
                              : theme.palette.mode === "dark"
                                ? theme.palette.primary.main
                                : "#000",
                          borderColor:
                            requestSubmitted || isSubmittingRequest
                              ? theme.palette.grey[400]
                              : theme.palette.mode === "dark"
                                ? theme.palette.primary.main
                                : "#000",
                          color: "#fff",
                          "&:hover": {
                            backgroundColor:
                              requestSubmitted ||
                              isSubmittingRequest ||
                              isLoadingMoreRC
                                ? theme.palette.grey[400]
                                : theme.palette.mode === "dark"
                                  ? theme.palette.primary.dark
                                  : "#222",
                            borderColor:
                              requestSubmitted ||
                              isSubmittingRequest ||
                              isLoadingMoreRC
                                ? theme.palette.grey[400]
                                : theme.palette.mode === "dark"
                                  ? theme.palette.primary.dark
                                  : "#222",
                            color: "#fff",
                          },
                          "&.Mui-disabled": {
                            color: "#fff",
                            opacity: 1,
                          },
                        }}
                      >
                        {isSubmittingRequest
                          ? "Submitting..."
                          : emptyState.actionText}
                      </CleanButton>
                    )}
                  </Box>
                </Box>
              </EmptyStateWrapper>
            )}{" "}
            {/* end of no-suggestions / has-suggestions conditional */}
            {/* Similar suggestions — shown when search found nobody */}
            {displayRawCandidates.length === 0 && suggestions.length > 0 && (
              <Box
                sx={{ mt: 4, width: "100%", maxWidth: "1200px", mx: "auto" }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: 2,
                    px: { xs: 1, md: 0 },
                    fontWeight: 500,
                  }}
                >
                  {suggestionLabel || "People you can meet"}
                </Typography>
                <Grid container spacing={2}>
                  {suggestions.map((profile) => (
                    <Grid
                      size={{ xs: 12, md: 6, lg: 4 }}
                      key={profile.id || profile.sub}
                    >
                      <ProfileCard
                        user={profile}
                        savedVariant
                        onViewProfile={(profileData) => {
                          router.push(
                            `/profile/${profileData.id || "unknown"}`,
                          );
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            {displayRawCandidates.length > 0 && (
              <CandidateList
                candidates={displayRawCandidates}
                showNarrowSearchHint={shouldShowTableNarrowHint}
                isLoadingMore={isLoadingMoreRC}
                enrichingId={enrichingId}
                enrichedUrls={enrichedUrls}
                onEnrich={onEnrichOne}
                onRefineSearch={onRefineSearch}
                query={(nameQuery || searchQuery).trim()}
                allowMissingCompany={isRoleLookup || isPersonLookup}
                narrowSearchHintMode={isRoleLookup ? "role" : "name"}
              />
            )}
            {displayRawCandidates.length > 0 && renderSeeMorePeople()}
          </Box>
        ) : (
          // Results display
          <>
            <Grid container spacing={{ xs: 1.5, md: 3 }}>
              {results.map((user, index) => (
                <Grid
                  size={{ xs: 12, md: 6, lg: 4 }}
                  key={`${user.objectID ||
                    user.id ||
                    user.sub ||
                    "u"}-${index}`}
                >
                  <Box sx={{ borderRadius: 0 }}>
                    <ProfileCard
                      user={user}
                      savedVariant
                      onViewProfile={(profileData) => {
                        router.push(
                          `/profile/${profileData.id || "unknown"}`,
                        );
                      }}
                    />
                  </Box>
                </Grid>
                ))}
              </Grid>

            {(moreResults.length > 0 || savedHasMore) && (
              <Box sx={{ mt: 3 }}>
                <Button
                  disableRipple
                  onClick={onShowMore}
                  disabled={isLoadingMoreRC}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    color: "primary.main",
                    textTransform: "none",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    px: 1,
                    "&:hover": { background: "transparent" },
                    "&:active": { background: "transparent" },
                  }}
                >
                  {isLoadingMoreRC ? "Loading more people..." : "Show more profiles"}
                </Button>
              </Box>
            )}

            {moreResults.length === 0 && !savedHasMore &&
              shouldOfferAnotherPerson && !showExternalCandidates && (
              <Box sx={{ mt: 3, textAlign: "center" }}>
                {externalSearchUnavailable && (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.text.primary,
                        mb: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      We couldn&apos;t load more results
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        mb: 1,
                      }}
                    >
                      Something went wrong while finding more results. Please
                      try again in a moment.
                    </Typography>
                  </>
                )}
                <Button
                  disableRipple
                  disabled={
                    isLoadingMoreRC ||
                    noMoreExternalCandidates ||
                    (cooldownActive && displayRawCandidates.length === 0)
                  }
                  onClick={async () => {
                    anotherPersonClickPendingRef.current = true;
                    if (displayRawCandidates.length > 0) {
                      setShowExternalCandidates(true);
                      return;
                    }

                    setIsRetryingSearch(externalSearchUnavailable);
                    try {
                      const loadedCandidates = await onLoadNextRCPage?.();
                      if (!loadedCandidates || loadedCandidates.stale) return;
                      const hasLoadedDisplayCandidates = (
                        loadedCandidates || []
                      ).some((candidate) => shouldShowCandidate(candidate));
                      if (hasLoadedDisplayCandidates) {
                        setShowExternalCandidates(true);
                      } else if (loadedCandidates?.externalSearchUnavailable) {
                        setExternalSearchUnavailable(true);
                      } else {
                        setExternalSearchUnavailable(false);
                        setNoMoreExternalCandidates(
                          !loadedCandidates.nextCursor && !loadedCandidates.savedCursor,
                        );
                      }
                    } finally {
                      setIsRetryingSearch(false);
                    }
                  }}
                  sx={{
                    color:
                      theme.palette.mode === "light"
                        ? theme.palette.background.accent
                        : "primary.main",
                    textTransform: "none",
                    fontSize: { xs: "1.1rem", sm: "1rem" },
                    fontWeight: 560,
                    px: 1,
                    opacity:
                      isLoadingMoreRC || noMoreExternalCandidates ? 0.65 : 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&:hover": {
                      background: "transparent",
                      textDecoration:
                        !isLoadingMoreRC && !noMoreExternalCandidates
                          ? "underline"
                          : "none",
                    },
                    "&:active": { background: "transparent" },
                  }}
                >
                  {externalSearchUnavailable ? (
                    "Try again"
                  ) : noMoreExternalCandidates ? (
                    "No other matches found"
                  ) : isLoadingMoreRC ? (
                    <LoadingProgressText
                      pulse={loadingTextProgress >= loadingLabel.length}
                    >
                      {Array.from(loadingLabel).map((char, index) => (
                        <LoadingProgressChar
                          key={`${char}-${index}`}
                          active={index < loadingTextProgress}
                        >
                          {char}
                        </LoadingProgressChar>
                      ))}
                    </LoadingProgressText>
                  ) : isPersonLookup ? (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.4 }}
                    >
                      <span>{`Find another ${displayNameQuery}?`}</span>
                      <ExpandMoreIcon
                        sx={{
                          fontSize: "1.2rem",
                          transition: "transform 0.2s ease",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.4 }}
                    >
                      <span>Find more people</span>
                      <ExpandMoreIcon
                        sx={{
                          fontSize: "1.2rem",
                          transition: "transform 0.2s ease",
                        }}
                      />
                    </Box>
                  )}
                </Button>
                {noMoreExternalCandidates && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color: theme.palette.text.secondary,
                      fontSize: "0.86rem",
                    }}
                  >
                    {isPersonLookup ? (
                      <>
                        No other matches found. Try searching with a company,
                        like{" "}
                        <Box
                          component="button"
                          type="button"
                          onClick={onRefineSearch}
                          sx={{
                            p: 0,
                            border: 0,
                            background: "transparent",
                            color: "primary.main",
                            cursor: "pointer",
                            font: "inherit",
                            textDecoration: "underline",
                            textUnderlineOffset: "2px",
                            "&:hover": { color: "primary.dark" },
                          }}
                        >
                          &ldquo;{`${displayNameQuery} at Company`}&rdquo;
                        </Box>
                        .
                      </>
                    ) : (
                      <>
                        No other matches found. Try adding a company or
                        location, like{" "}
                        <Box
                          component="button"
                          type="button"
                          onClick={onRefineSearch}
                          sx={{
                            p: 0,
                            border: 0,
                            background: "transparent",
                            color: "primary.main",
                            cursor: "pointer",
                            font: "inherit",
                            textDecoration: "underline",
                            textUnderlineOffset: "2px",
                            "&:hover": { color: "primary.dark" },
                          }}
                        >
                          &ldquo;{`${displayNameQuery} in San Francisco`}
                          &rdquo;
                        </Box>
                        .
                      </>
                    )}
                  </Typography>
                )}
              </Box>
            )}

            {moreResults.length === 0 && showExternalCandidates && (
              <Box
                ref={candidateListTopRef}
                sx={{
                  mt: showExternalCandidates ? 5 : 0,
                  scrollMarginTop: { xs: "128px", sm: "112px" },
                }}
              >
                <CandidateList
                  candidates={displayRawCandidates}
                  showNarrowSearchHint={
                    shouldShowTableNarrowHint && noMoreExternalCandidates
                  }
                  isLoadingMore={isLoadingMoreRC}
                  enrichingId={enrichingId}
                  enrichedUrls={enrichedUrls}
                  onEnrich={onEnrichOne}
                  onRefineSearch={onRefineSearch}
                  query={(nameQuery || searchQuery).trim()}
                  allowMissingCompany={isRoleLookup || isPersonLookup}
                  narrowSearchHintMode={isRoleLookup ? "role" : "name"}
                />

                {renderSeeMorePeople()}
              </Box>
            )}
          </>
        )}
      </Container>
      <ConfirmDialog
        open={showEmailDialog}
        onClose={() => setShowEmailDialog(false)}
        onConfirm={submitNotificationEmail}
        title="How can we notify you?"
        body={
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Email address"
            value={notificationEmail}
            onChange={(event) => {
              setNotificationEmail(event.target.value);
              setEmailError("");
            }}
            error={Boolean(emailError)}
            helperText={
              emailError ||
              "Optional. We'll email you when their profile is available."
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                submitNotificationEmail();
              }
            }}
          />
        }
        showConfirmButton={true}
        showIgnoreButton={true}
        confirmButtonText="Notify Me"
        ignoreButtonText="Cancel"
        showCloseIcon={true}
        isProcessing={isSubmittingRequest}
        processingText="Saving..."
        modalStyle="toast"
        disableBackdropClick={true}
        disableEscapeKeyDown={false}
      />
    </>
  );
};

export default SearchResults;
